import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserLiveTranscription } from 'src/components/Assistant/live-transcript/live-transcript.operations';
import {
  LIVE_TRANSCRIPTION_FALLBACK_POLL_INTERVAL_MS,
  LIVE_TRANSCRIPTION_SEGMENTS_LIMIT,
  LiveTranscriptionState,
  LiveTranscriptRole,
  LiveTranscriptSegment,
  NormalizedLiveTranscriptionPayload,
  UseLiveTranscriptionInput,
} from 'src/components/Assistant/live-transcript/live-transcript.types';
import {
  buildSegmentKey,
  matchUserFinishedEvent,
  matchUserSegmentEvent,
  mergeSegmentsByOrder,
} from 'src/components/Assistant/live-transcript/live-transcript.utils';
import { wsEventBus } from 'src/core/realtime-connection';
import { LiveTranscriptionPaginationDirection } from 'src/gql/graphql';
import { useCurrentUserProfile } from 'src/graphql';

const EMPTY_STATE: LiveTranscriptionState = {
  segments: [],
  isStreaming: false,
  isFinished: false,
  transcriptionId: null,
  externalTranscriptionId: null,
};

type UserLoadTranscriptionFn = ReturnType<typeof useUserLiveTranscription>[0];

const loadUserTranscriptionPage = async (
  loadUserTranscription: UserLoadTranscriptionFn,
  cursor: string | null,
): Promise<NormalizedLiveTranscriptionPayload | null> => {
  const { data } = await loadUserTranscription({
    variables: {
      params: {
        limit: LIVE_TRANSCRIPTION_SEGMENTS_LIMIT,
        direction: LiveTranscriptionPaginationDirection.Forward,
        cursor,
      },
    },
  });

  const payload = data?.getUserLiveTranscription;

  if (!payload) {
    return null;
  }

  return {
    transcriptionId: payload.transcriptionId,
    externalTranscriptionId: payload.externalTranscriptionId,
    isFinished: payload.isFinished,
    cursor: payload.cursor ?? null,
    segments: payload.segments
      .filter((segment): segment is NonNullable<typeof segment> => segment !== null)
      .map((segment) => ({
        // Schema types `role` as a raw String; the backend only ever sends 'agent' | 'client',
        // the same values the FE-only LiveTranscriptRole enum models for the conversation query.
        role: segment.role as LiveTranscriptRole,
        timestamp: segment.timestamp,
        transcriptionText: segment.transcriptionText,
        start: segment.start,
        duration: segment.duration,
      })),
  };
};

export const useLiveTranscription = ({
  enabled,
}: UseLiveTranscriptionInput): LiveTranscriptionState => {
  const [loadUserTranscription] = useUserLiveTranscription();
  const { user } = useCurrentUserProfile();
  const currentUserId = user?.userId;

  const scopeKey = `${enabled ? '1' : '0'}:${currentUserId ?? ''}`;

  const [scopedState, setScopedState] = useState<{
    scopeKey: string;
    value: LiveTranscriptionState;
  }>({
    scopeKey,
    value: EMPTY_STATE,
  });

  const cursorRef = useRef<string | null>(null);
  const seenSegmentsRef = useRef<Set<string>>(new Set());
  const isPendingRef = useRef(false);
  const fallbackIntervalRef = useRef<number | null>(null);
  // Which call the paging cursor belongs to. The effect no longer remounts between calls, so
  // nothing else would tell a cursor minted for the previous call apart from a current one.
  const activeTranscriptionIdRef = useRef<string | null>(null);

  const state = scopedState.scopeKey === scopeKey ? scopedState.value : EMPTY_STATE;

  const setStateForScope = useCallback(
    (
      updater:
        | LiveTranscriptionState
        | ((previous: LiveTranscriptionState) => LiveTranscriptionState),
    ) => {
      setScopedState((previousScopedState) => {
        const previousState =
          previousScopedState.scopeKey === scopeKey ? previousScopedState.value : EMPTY_STATE;
        const nextState = typeof updater === 'function' ? updater(previousState) : updater;

        return {
          scopeKey,
          value: nextState,
        };
      });
    },
    [scopeKey],
  );

  useEffect(() => {
    if (!enabled || currentUserId === undefined) {
      return;
    }

    let isActive = true;

    const stopFallbackPolling = () => {
      if (fallbackIntervalRef.current !== null) {
        window.clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };

    const hydrateFromSource = async (input: { useCursor: boolean }): Promise<boolean> => {
      if (!isActive || isPendingRef.current) {
        return false;
      }

      isPendingRef.current = true;

      try {
        const payload = await loadUserTranscriptionPage(
          loadUserTranscription,
          input.useCursor ? cursorRef.current : null,
        );

        if (!isActive) {
          return false;
        }

        if (!payload) {
          setStateForScope((previous) => {
            if (!previous.transcriptionId) {
              return EMPTY_STATE;
            }

            return {
              ...previous,
              isStreaming: false,
              isFinished: true,
            };
          });

          return false;
        }

        const mappedSegments: LiveTranscriptSegment[] = [];

        for (const segment of payload.segments) {
          const segmentKey = buildSegmentKey({
            externalTranscriptionId: payload.externalTranscriptionId,
            timestamp: segment.timestamp,
            role: segment.role,
            transcriptionText: segment.transcriptionText,
          });

          if (seenSegmentsRef.current.has(segmentKey)) {
            continue;
          }

          seenSegmentsRef.current.add(segmentKey);
          mappedSegments.push({
            id: segmentKey,
            role: segment.role,
            timestamp: segment.timestamp,
            transcriptionText: segment.transcriptionText,
            start: segment.start ?? undefined,
            duration: segment.duration ?? undefined,
          });
        }

        const isSameTranscription =
          activeTranscriptionIdRef.current === null ||
          activeTranscriptionIdRef.current === payload.externalTranscriptionId;

        setStateForScope((previous) => {
          const baseSegments = isSameTranscription ? previous.segments : [];
          const isFinished =
            payload.isFinished || (isSameTranscription && previous.isFinished);

          return {
            segments: mergeSegmentsByOrder(baseSegments, mappedSegments),
            isStreaming: !isFinished,
            isFinished,
            transcriptionId: payload.transcriptionId,
            externalTranscriptionId: payload.externalTranscriptionId,
          };
        });

        activeTranscriptionIdRef.current = payload.externalTranscriptionId;
        cursorRef.current = payload.cursor;

        return true;
      } catch {
        return false;
      } finally {
        // Only the still-active effect run may release the guard: a call abandoned by cleanup
        // resolves after the next run already took it, and would hand out a second in-flight slot.
        if (isActive) {
          isPendingRef.current = false;
        }
      }
    };

    const startFallbackPolling = () => {
      if (fallbackIntervalRef.current !== null) {
        return;
      }

      fallbackIntervalRef.current = window.setInterval(() => {
        void hydrateFromSource({ useCursor: true });
      }, LIVE_TRANSCRIPTION_FALLBACK_POLL_INTERVAL_MS);
    };

    // Custom transcription events arrive as { data: { type, ...payload } } with no `operation`
    // field, so onOperation() cannot be used here — we must filter on data.type directly.
    const segmentSub = wsEventBus.asObservable().subscribe({
      next: (rawEvent) => {
        const segmentEvent = matchUserSegmentEvent(rawEvent.data, currentUserId);

        if (!segmentEvent) {
          return;
        }

        const segmentKey = buildSegmentKey({
          externalTranscriptionId: segmentEvent.externalTranscriptionId,
          timestamp: segmentEvent.timestamp,
          role: segmentEvent.role,
          transcriptionText: segmentEvent.transcriptionText,
        });

        if (seenSegmentsRef.current.has(segmentKey)) {
          return;
        }

        seenSegmentsRef.current.add(segmentKey);

        const nextSegment: LiveTranscriptSegment = {
          id: segmentKey,
          role: segmentEvent.role,
          timestamp: segmentEvent.timestamp,
          transcriptionText: segmentEvent.transcriptionText,
          start: segmentEvent.start,
          duration: segmentEvent.duration,
        };

        const isNewTranscription =
          activeTranscriptionIdRef.current !== null &&
          activeTranscriptionIdRef.current !== segmentEvent.externalTranscriptionId;

        if (isNewTranscription) {
          cursorRef.current = null;
        }

        activeTranscriptionIdRef.current = segmentEvent.externalTranscriptionId;

        setStateForScope((previous) => {
          const baseSegments = isNewTranscription ? [] : previous.segments;

          return {
            segments: mergeSegmentsByOrder(baseSegments, [nextSegment]),
            isStreaming: true,
            isFinished: false,
            transcriptionId: segmentEvent.transcriptionId,
            externalTranscriptionId: segmentEvent.externalTranscriptionId,
          };
        });
      },
      error: (err: unknown) => console.error('WS live transcription segment error:', err),
    });

    const finishedSub = wsEventBus.asObservable().subscribe({
      next: (rawEvent) => {
        const finishedEvent = matchUserFinishedEvent(rawEvent.data, currentUserId);

        if (!finishedEvent) {
          return;
        }

        setStateForScope((previous) => {
          if (
            previous.externalTranscriptionId &&
            finishedEvent.externalTranscriptionId &&
            previous.externalTranscriptionId !== finishedEvent.externalTranscriptionId
          ) {
            return previous;
          }

          return {
            ...previous,
            isStreaming: false,
            isFinished: true,
            transcriptionId: finishedEvent.transcriptionId,
            externalTranscriptionId:
              finishedEvent.externalTranscriptionId ?? previous.externalTranscriptionId,
          };
        });

        stopFallbackPolling();
      },
      error: (err: unknown) => console.error('WS live transcription finished error:', err),
    });

    const disconnectSub = wsEventBus.onDisconnect().subscribe(() => {
      startFallbackPolling();
      void hydrateFromSource({ useCursor: true });
    });

    const reconnectSub = wsEventBus.onReconnect().subscribe(() => {
      stopFallbackPolling();
      void hydrateFromSource({ useCursor: true });
    });

    const subs = [segmentSub, finishedSub, disconnectSub, reconnectSub];

    void (async () => {
      let hasMore = await hydrateFromSource({ useCursor: false });
      while (isActive && hasMore && cursorRef.current) {
        hasMore = await hydrateFromSource({ useCursor: true });
      }
    })();

    return () => {
      isActive = false;
      stopFallbackPolling();
      cursorRef.current = null;
      activeTranscriptionIdRef.current = null;
      seenSegmentsRef.current = new Set();
      isPendingRef.current = false;
      subs.forEach((s) => s.unsubscribe());
      setStateForScope(EMPTY_STATE);
    };
  }, [currentUserId, enabled, loadUserTranscription, scopeKey, setStateForScope]);

  return state;
};
